import React from 'react';
import { ArrowRight, TrendingUp, DollarSign, Calendar, Users, Gift } from 'lucide-react';
import { Link } from 'react-router-dom';

const InvestmentPackages = () => {
  const packages = [
    {
      name: 'Basic Plan',
      image: '/packages/money-2696228_640.jpg',
      minAmount: '$100',
      maxAmount: '$999',
      roi: '15%',
      duration: '24 Hours',
      referralCommission: '5%',
      instantReward: null,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-gold-600'
    },
    {
      name: 'Gold Plan',
      image: '/packages/gold-163519_1280.jpg',
      minAmount: '$1,000',
      maxAmount: '$4,999',
      roi: '30%',
      duration: '48 Hours',
      referralCommission: '10%',
      instantReward: null,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-gold-600'
    },
    {
      name: 'Master Plan',
      image: '/images/chart-1905224_640.jpg',
      minAmount: '$5,000',
      maxAmount: '$9,999',
      roi: '45%',
      duration: '72 Hours',
      referralCommission: '15%',
      instantReward: null,
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-500'
    },
    {
      name: 'Premium Plan',
      image: '/images/sa-rapita-2483668_640.jpg',
      minAmount: '$10,000',
      maxAmount: '$49,999',
      roi: '60%',
      duration: '96 Hours',
      referralCommission: '20%',
      instantReward: null,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-gold-600'
    },
    {
      name: 'VIP Plan',
      image: '/packages/money-2696229_640.jpg',
      minAmount: '$50,000',
      maxAmount: 'Unlimited',
      roi: '75%',
      duration: '120 Hours',
      referralCommission: '25%',
      instantReward: null,
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-500'
    }
  ];

  return (
    <section id="investment-packages" className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-10 w-64 h-64 bg-gold-400 rounded-full opacity-20"></div>
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-orange-400 rounded-full opacity-15"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <div className="inline-flex items-center px-4 py-2 bg-gold-100 rounded-full mb-6">
            <TrendingUp className="h-4 w-4 text-gold-600 mr-2" />
            <span className="text-gold-700 text-sm font-semibold">Investment Opportunities</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
            Investment <span className="text-gradient">Packages</span>
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Choose from our diverse range of investment packages designed to meet your financial goals. 
            Each package offers competitive returns and flexible terms to suit your investment needs.
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {packages.map((pkg, index) => (
            <div
              key={pkg.name}
              className={`group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${pkg.borderColor} overflow-hidden`}
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={pkg.image}
                  alt={pkg.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white drop-shadow-lg">{pkg.name}</h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-600">
                      <DollarSign className="h-4 w-4 mr-2" />
                      <span className="text-sm">Min Amount:</span>
                    </div>
                    <span className="font-semibold text-navy-900">{pkg.minAmount}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-600">
                      <DollarSign className="h-4 w-4 mr-2" />
                      <span className="text-sm">Max Amount:</span>
                    </div>
                    <span className="font-semibold text-navy-900">{pkg.maxAmount}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-600">
                      <TrendingUp className="h-4 w-4 mr-2" />
                      <span className="text-sm">ROI:</span>
                    </div>
                    <span className={`font-bold ${pkg.textColor}`}>{pkg.roi}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-600">
                      <Calendar className="h-4 w-4 mr-2" />
                      <span className="text-sm">Duration:</span>
                    </div>
                    <span className="font-semibold text-navy-900">{pkg.duration}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-600">
                      <Users className="h-4 w-4 mr-2" />
                      <span className="text-sm">Referral Commission:</span>
                    </div>
                    <span className="font-semibold text-navy-900">{pkg.referralCommission}</span>
                  </div>

                  {pkg.instantReward && (
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <div className="flex items-center text-gray-600">
                        <Gift className="h-4 w-4 mr-2" />
                        <span className="text-sm">Instant Reward:</span>
                      </div>
                      <span className={`font-bold ${pkg.textColor}`}>{pkg.instantReward}</span>
                    </div>
                  )}
                </div>

                {/* CTA */}
                <a
                  href="https://online.amdconsults.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group/btn w-full inline-flex items-center justify-center px-6 py-3 ${pkg.bgColor} ${pkg.textColor} font-semibold rounded-lg hover:shadow-md transition-all duration-300`}
                >
                  Invest Now
                  <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12" data-aos="fade-up" data-aos-delay="300">
          <a
            href="https://online.amdconsults.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-gold-600 hover:bg-gold-700 text-white font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
          >
            View All Packages
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default InvestmentPackages;

